import React, {CSSProperties, FC, memo, useMemo} from 'react';
import {Media, MontanaMediaRatios} from "@montana/shared/media";
import {Appear} from "@montana/shared/appear";
import {IColorContentPage} from "@montana/models/generated/cms.interface";
import {isBrowser} from "@montana/helpers/browser.helper";
import {getTextColorFor} from "@montana/helpers/theme-color.helper";
import {Breadcrumbs} from "@montana/shared/breadcrumbs";
import {RichText} from "@montana/shared/rich-text";

const HERO_FIRST_APPEAR_DELAY_MS = 400;

export type HeroLayouts = 'landscape' | 'square' | 'no-image' | 'portrait' | 'wide';

interface IMediaSizes {
    'landscape'?: string;
    'square'?: string;
    'no-image'?: string;
    'portrait'?: string;
    'wide'?: string;
}

const mediaSizes: IMediaSizes = {
    portrait: [
        '(min-width: 1680px) 470px',
        '(min-width: 1367px) 411px',
        '(min-width: 811px) 30vw',
        '(min-width: 480px) 30vw',
        '80vw'
    ].join(', '),
    wide: '100vw',
}

export interface IHeroColourPageModuleProps extends Partial<Pick<IColorContentPage, 'image' | 'quoteAuthor' | 'backgroundColorHex' | 'color' | 'quote'>> {
    backgroundColorHex?: string;
    isFirstRendering?: boolean;
}

export const HeroColourPageModule: FC<IHeroColourPageModuleProps> = memo((props: IHeroColourPageModuleProps) => {
    const {
        backgroundColorHex = '#ffffff',
        image,
        isFirstRendering = true,
        color,
        quoteAuthor,
        quote
    } = props;

    const heroLayout: HeroLayouts = (image
        ? 'portrait'
        : 'no-image') as HeroLayouts;

    const style = {
        '--background-color': backgroundColorHex,
        '--swatch-color': getTextColorFor(backgroundColorHex),
    } as CSSProperties;

    const colId = useMemo(() => color?.colorId?.split('-')?.[0], [color?.colorId]);
    const colorNameWithoutNumber = useMemo(() => color?.colorName?.replace(/\d+/g, '').trim(), [color?.colorName]);
    return (
        <>
            <section className="color-hero-block" style={style}>
                <div className="color-hero-block__bg" style={{
                    animationDelay: isFirstRendering ? `${HERO_FIRST_APPEAR_DELAY_MS}ms` : ''
                }}></div>
                <div className="color-hero-block__content">
                    {image && (
                        <div className="color-hero-block__media-container">
                            <Media
                                image={image}
                                mediaRatio={MontanaMediaRatios.ColorHero}
                                placeholder={heroLayout !== 'wide'}
                                animate={true}
                                sizes={mediaSizes[heroLayout]}
                                eager={true}
                            />
                            <div className="color-hero-block__swatch"><Media image={color?.colorSwatchImage}
                                                                             mediaRatio={MontanaMediaRatios.Square}
                                                                             eager={true} animate={false}/></div>
                        </div>
                    )}
                    <div className="color-hero-block__spacer"></div>
                    <div className="color-hero-block__text">
                        <div className="display-6">{colId}<h1 className="display-3">{colorNameWithoutNumber}</h1></div>
                        <blockquote className="color-hero-block__quote">
                            <RichText className="body-xl-optional" text={quote || ''}></RichText>
                            <RichText text={quoteAuthor || ''}></RichText>
                        </blockquote>
                        {isBrowser && (
                            <Appear delay={isFirstRendering ? HERO_FIRST_APPEAR_DELAY_MS : 0}
                                    className="color-hero-block__breadcrumbs">
                                <Breadcrumbs />
                            </Appear>

                        )}
                    </div>
                </div>
            </section>
        </>
    );
});
